/**
 * La rejilla: columnas = salas, filas = horas, y en la casilla la persona que estuvo
 * allí (MVP §6.2, con los ejes que pidió el propietario del producto).
 *
 * La casilla del crimen —sala y hora del cuerpo— es **una sola casilla**, marcada con la
 * calavera y el acento cálido reservado a eso. Quien escribas ahí es tu acusación: es el
 * centro de gravedad de la pantalla y por eso la rejilla se pinta así.
 *
 * Decisiones que sostienen el caso N=6 en 360 px:
 * - Columna de horas fija (`sticky left-0`) y el resto con scroll horizontal.
 * - Casillas de 44 px mínimo, que es el objetivo táctil del §9.
 * - Dentro de la casilla solo cabe el retrato; el nombre completo va en el `aria-label`,
 *   así que el lector de pantalla anuncia hora, sala y persona.
 *
 * Nunca se bloquea una jugada: los conflictos se señalan (borde y aspa de aviso) y el
 * jugador decide. El cálculo de conflicto es O(1) por casilla — ver `state.ts`.
 */

import { useRef, useState } from 'react';
import { Skull, TriangleAlert } from 'lucide-react';

import { Portrait } from '../design/Portrait';
import { InitialBadge } from './InitialBadge';
import type { PickerMode } from './PersonPicker';
import { PersonPicker } from './PersonPicker';
import type { CaseState } from './state';
import { cellConflict, cellNotes, cellPerson } from './state';
import type { PersonVisual, RoomVisual } from './visuals';
import { matchByKey } from './visuals';

/** Milisegundos de pulsación larga que abren el modo anotación en móvil. */
const LONG_PRESS_MS = 450;

export interface CaseGridProps {
  state: CaseState;
  people: readonly PersonVisual[];
  hours: readonly string[];
  rooms: readonly RoomVisual[];
  crime: { room: number; hour: number };
  /** Casillas resaltadas por la pista seleccionada, como claves `hora:sala`. */
  highlighted: ReadonlySet<string>;
  onPlace: (hour: number, room: number, person: number) => void;
  onClear: (hour: number, room: number) => void;
  onToggleNote: (hour: number, room: number, person: number) => void;
}

interface OpenPicker {
  hour: number;
  room: number;
  mode: PickerMode;
}

export function CaseGrid({
  state,
  people,
  hours,
  rooms,
  crime,
  highlighted,
  onPlace,
  onClear,
  onToggleNote,
}: CaseGridProps) {
  const n = state.progress.n;
  const cellsRef = useRef(new Map<string, HTMLButtonElement>());
  const longPressRef = useRef<{ timer: number | null; fired: boolean }>({
    timer: null,
    fired: false,
  });
  const [focused, setFocused] = useState<{ hour: number; room: number }>({
    hour: 0,
    room: 0,
  });
  const [picker, setPicker] = useState<OpenPicker | null>(null);

  const key = (hour: number, room: number) => `${hour}:${room}`;
  const names = people.map((one) => one.name);

  function focusCell(hour: number, room: number) {
    const next = {
      hour: Math.max(0, Math.min(n - 1, hour)),
      room: Math.max(0, Math.min(n - 1, room)),
    };
    setFocused(next);
    cellsRef.current.get(key(next.hour, next.room))?.focus();
  }

  function labelFor(hour: number, room: number): string {
    const person = cellPerson(state, hour, room);
    const notes = cellNotes(state, hour, room);
    const conflict = cellConflict(state, hour, room);
    const isCrime = hour === crime.hour && room === crime.room;

    const parts = [
      `${hours[hour] ?? ''}, ${rooms[room]?.name ?? ''}:`,
      person === null ? 'casilla vacía' : (people[person]?.name ?? ''),
    ];
    if (isCrime) parts.push('(aquí aparece el cuerpo)');
    if (conflict !== null) {
      parts.push(
        conflict.row && conflict.column
          ? '(conflicto de fila y de columna)'
          : conflict.row
            ? '(conflicto: ya está en otra sala a esa hora)'
            : '(conflicto: ya ha pasado por esa sala)',
      );
    }
    if (notes.length > 0) {
      parts.push(
        `(descartados: ${notes.map((p) => people[p]?.name ?? '').join(', ')})`,
      );
    }
    return parts.join(' ');
  }

  function cancelLongPress() {
    if (longPressRef.current.timer !== null) {
      window.clearTimeout(longPressRef.current.timer);
      longPressRef.current.timer = null;
    }
  }

  function onCellKeyDown(
    event: React.KeyboardEvent<HTMLButtonElement>,
    hour: number,
    room: number,
  ) {
    switch (event.key) {
      case 'ArrowUp':
      case 'ArrowDown':
      case 'ArrowLeft':
      case 'ArrowRight': {
        event.preventDefault();
        const delta = {
          ArrowUp: [-1, 0],
          ArrowDown: [1, 0],
          ArrowLeft: [0, -1],
          ArrowRight: [0, 1],
        }[event.key] ?? [0, 0];
        focusCell(hour + (delta[0] ?? 0), room + (delta[1] ?? 0));
        return;
      }
      case 'Home':
        event.preventDefault();
        focusCell(hour, 0);
        return;
      case 'End':
        event.preventDefault();
        focusCell(hour, n - 1);
        return;
      case 'Backspace':
      case 'Delete':
        event.preventDefault();
        onClear(hour, room);
        return;
      default:
        break;
    }

    // Letra o número eligen persona sin abrir el popover; con Shift, la descartan.
    if (event.ctrlKey || event.metaKey || event.altKey) return;
    const person = matchByKey(names, event.key, cellPerson(state, hour, room));
    if (person === null) return;
    event.preventDefault();
    if (event.shiftKey) onToggleNote(hour, room, person);
    else onPlace(hour, room, person);
  }

  const gridTemplate = {
    gridTemplateColumns: `var(--mk-grid-hour-col) repeat(${n}, minmax(44px, 1fr))`,
  };

  const openCell =
    picker === null ? null : cellsRef.current.get(key(picker.hour, picker.room));

  return (
    <div className="border-border bg-surface overflow-x-auto rounded-lg border">
      <div
        role="grid"
        aria-label="Rejilla del caso"
        className="grid"
        style={gridTemplate}
      >
        <div role="row" className="contents">
          <div
            role="columnheader"
            className="bg-surface border-border sticky left-0 z-20 border-r border-b px-2 py-2 text-xs uppercase"
          >
            <span className="text-text-muted">Hora</span>
          </div>
          {rooms.map((room, roomIndex) => {
            const Icon = room.icon;
            const isCrimeRoom = roomIndex === crime.room;
            return (
              <div
                key={room.name}
                role="columnheader"
                className={`border-border flex flex-col items-center gap-1 border-r border-b px-1 py-2 ${
                  isCrimeRoom ? 'bg-crime/10' : ''
                }`}
              >
                <span
                  className={`grid h-8 w-8 place-items-center rounded-md ${
                    room.fg === 'ink' ? 'text-ink' : 'text-paper'
                  }`}
                  style={{ backgroundColor: room.colorVar }}
                >
                  <Icon size={18} strokeWidth={2.25} aria-hidden="true" />
                </span>
                {/* El nombre completo no cabe en 44 px; el lector siempre lo tiene. */}
                <span className="hidden text-center text-xs leading-tight sm:block">
                  {room.name}
                </span>
                <span className="sr-only">{room.name}</span>
              </div>
            );
          })}
        </div>

        {hours.map((hour, hourIndex) => (
          <div role="row" className="contents" key={hour}>
            <div
              role="rowheader"
              className={`bg-surface border-border sticky left-0 z-20 flex items-center border-r border-b px-2 ${
                hourIndex === crime.hour ? 'text-crime font-semibold' : 'text-text'
              }`}
            >
              <span className="font-display sm:text-hour text-sm">{hour}</span>
            </div>

            {rooms.map((_, roomIndex) => {
              const person = cellPerson(state, hourIndex, roomIndex);
              const notes = cellNotes(state, hourIndex, roomIndex);
              const conflict = cellConflict(state, hourIndex, roomIndex);
              const visual = person === null ? undefined : people[person];
              const isFocused =
                focused.hour === hourIndex && focused.room === roomIndex;
              const isHighlighted = highlighted.has(key(hourIndex, roomIndex));
              const isCrime = hourIndex === crime.hour && roomIndex === crime.room;

              return (
                <div
                  role="gridcell"
                  key={roomIndex}
                  className={`border-border relative border-r border-b ${
                    isCrime
                      ? 'bg-crime/25'
                      : roomIndex === crime.room
                        ? 'bg-crime/6'
                        : ''
                  }`}
                >
                  <button
                    type="button"
                    ref={(element) => {
                      const map = cellsRef.current;
                      if (element === null) map.delete(key(hourIndex, roomIndex));
                      else map.set(key(hourIndex, roomIndex), element);
                    }}
                    tabIndex={isFocused ? 0 : -1}
                    aria-label={labelFor(hourIndex, roomIndex)}
                    aria-haspopup="dialog"
                    onFocus={() => setFocused({ hour: hourIndex, room: roomIndex })}
                    onKeyDown={(event) => onCellKeyDown(event, hourIndex, roomIndex)}
                    onContextMenu={(event) => {
                      event.preventDefault();
                      setPicker({ hour: hourIndex, room: roomIndex, mode: 'note' });
                    }}
                    onPointerDown={() => {
                      longPressRef.current.fired = false;
                      cancelLongPress();
                      longPressRef.current.timer = window.setTimeout(() => {
                        longPressRef.current.fired = true;
                        setPicker({ hour: hourIndex, room: roomIndex, mode: 'note' });
                      }, LONG_PRESS_MS);
                    }}
                    onPointerUp={cancelLongPress}
                    onPointerLeave={cancelLongPress}
                    onPointerCancel={cancelLongPress}
                    onClick={() => {
                      if (longPressRef.current.fired) {
                        longPressRef.current.fired = false;
                        return;
                      }
                      setPicker({ hour: hourIndex, room: roomIndex, mode: 'place' });
                    }}
                    className={`focus-ring relative grid h-11 w-full min-w-11 place-items-center ${
                      isHighlighted ? 'ring-accent ring-2 ring-inset' : ''
                    } ${conflict !== null ? 'outline-crime outline-2 -outline-offset-2' : ''}`}
                  >
                    {visual !== undefined ? (
                      <span className="relative">
                        <Portrait portrait={visual.portrait} size="sm" />
                        <InitialBadge initials={visual.initials} />
                      </span>
                    ) : (
                      <>
                        {isCrime && (
                          <Skull
                            size={20}
                            strokeWidth={2.25}
                            aria-hidden="true"
                            className="text-crime"
                          />
                        )}
                        <NoteMarks notes={notes} people={people} />
                      </>
                    )}
                    {conflict !== null && (
                      <TriangleAlert
                        size={12}
                        aria-hidden="true"
                        className="text-crime absolute top-0.5 right-0.5"
                      />
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {picker !== null && (
        <PersonPicker
          people={people}
          anchor={openCell ?? null}
          mode={picker.mode}
          onModeChange={(mode) => setPicker({ ...picker, mode })}
          current={cellPerson(state, picker.hour, picker.room)}
          notes={cellNotes(state, picker.hour, picker.room)}
          cellLabel={`${rooms[picker.room]?.name ?? ''} a las ${hours[picker.hour] ?? ''}`}
          onPlace={(person) => onPlace(picker.hour, picker.room, person)}
          onToggleNote={(person) => onToggleNote(picker.hour, picker.room, person)}
          onClear={() => onClear(picker.hour, picker.room)}
          onClose={() => {
            setPicker(null);
            focusCell(picker.hour, picker.room);
          }}
        />
      )}
    </div>
  );
}

/** Anotaciones a lápiz: iniciales de las personas descartadas en la casilla. */
function NoteMarks({
  notes,
  people,
}: {
  notes: readonly number[];
  people: readonly PersonVisual[];
}) {
  if (notes.length === 0) return null;
  return (
    <span className="text-text pointer-events-none grid grid-cols-3 gap-x-0.5 font-mono text-[0.5625rem] leading-none opacity-60">
      {notes.slice(0, 6).map((person) => (
        <s key={person}>{people[person]?.initials ?? '?'}</s>
      ))}
    </span>
  );
}
